import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import LotteryWeekSelector, {
  getInitialWeekStart,
  getWeekRange,
} from '../../../components/LotteryWeekSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import apiClient from '../../../utils/axiosClient';
import type { WinningDataResponse } from '../../../types/luckyLottery.types';

dayjs.locale('ko');

const STORAGE_KEY = 'avatye.ops.lucky-lottery.winning.filter';

type StoredFilter = { startDate: string; endDate: string; appName: string };

const getStoredFilter = (): StoredFilter | null => {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s) return JSON.parse(s) as StoredFilter;
  } catch {
    /* ignore */
  }
  return null;
};

const saveFilter = (f: StoredFilter) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(f));
  } catch {
    /* ignore */
  }
};

const RANK_STYLE: Record<number, { bg: string; color: string }> = {
  1: { bg: '#fef9c3', color: '#92400e' },
  2: { bg: '#f1f5f9', color: '#475569' },
  3: { bg: '#fdf0e0', color: '#92400e' },
  4: { bg: '#ede9fe', color: '#5b21b6' },
  5: { bg: '#dcfce7', color: '#166534' },
};

function StatCard({
  label,
  value,
  unit,
  tooltip,
}: {
  label: string;
  value: string;
  unit?: string;
  tooltip?: string;
}) {
  return (
    <Box sx={{ backgroundColor: '#f8fafc', borderRadius: 3, p: 2.5, border: '1px solid #f1f5f9' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography sx={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="right">
            <InfoOutlinedIcon sx={{ fontSize: 15, color: '#9ca3af', cursor: 'pointer' }} />
          </Tooltip>
        )}
      </Box>
      <Typography sx={{ fontSize: '1.55rem', fontWeight: 700, color: '#0044D2', lineHeight: 1.2 }}>
        {value}
      </Typography>
      {unit && (
        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>{unit}</Typography>
      )}
    </Box>
  );
}

function RateBar({ rate }: { rate: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, height: 6, bgcolor: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        <Box
          sx={{
            width: `${Math.min(rate, 100)}%`,
            height: '100%',
            bgcolor: '#0044D2',
            borderRadius: 3,
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: '0.82rem',
          fontWeight: 600,
          color: '#1e293b',
          minWidth: 48,
          textAlign: 'right',
        }}
      >
        {rate.toFixed(1)}%
      </Typography>
    </Box>
  );
}

const fmt = (n: number) => n.toLocaleString('ko-KR');

export default function LuckyLotteryWinning() {
  const stored = useMemo(() => getStoredFilter(), []);
  const initialWeek = useMemo(() => {
    if (stored?.startDate && stored?.endDate)
      return { startDate: stored.startDate, endDate: stored.endDate };
    const { start, end } = getWeekRange(getInitialWeekStart());
    return { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') };
  }, [stored]);

  const [startDate, setStartDate] = useState(initialWeek.startDate);
  const [endDate, setEndDate] = useState(initialWeek.endDate);
  const [appName, setAppName] = useState<string>(stored?.appName ?? '앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [selectedOs, setSelectedOs] = useState<string>('all');
  const [data, setData] = useState<WinningDataResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchAppOptions = useCallback(() => {
    if (!startDate || !endDate) return;
    apiClient
      .get<string[]>('/api/v1/stat/lucky-lottery/app/list', {
        params: { start_date: startDate, end_date: endDate },
      })
      .then((res) => setAppOptions(['앱 전체', ...(res.data || [])]))
      .catch(() => undefined);
  }, [startDate, endDate]);

  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;
    const app = appName === '앱 전체' ? null : appName;
    saveFilter({ startDate, endDate, appName });
    setLoading(true);
    apiClient
      .get<WinningDataResponse>('/api/v1/analytics/lucky-lottery/winning', {
        params: {
          start_date: startDate,
          end_date: endDate,
          ...(app ? { app_name: app } : {}),
          ...(selectedOs !== 'all' ? { os_type: selectedOs } : {}),
        },
      })
      .then((res) => {
        setData(res.data);
        setErrorMessage('');
      })
      .catch(() => {
        setData(null);
        setErrorMessage('데이터 로드에 실패했습니다.');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, appName, selectedOs]);

  useEffect(() => {
    fetchAppOptions();
  }, [fetchAppOptions]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const ranks = data?.ranks ?? [];
  const nonWinning = data?.nonWinning ?? null;
  const summary = data?.summary ?? null;

  return (
    <Box>
      {/* 상단 필터 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1.5, md: 0 },
        }}
      >
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <LotteryWeekSelector
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        </Box>
        <Box
          sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 1.5, md: 0 }, width: { xs: '100%', md: 'auto' } }}
        >
          <FilterSelectorWithApp
            selectedApp={appName}
            appOptions={appOptions}
            onAppChange={(v) => setAppName(v ?? '앱 전체')}
            onSearch={fetchData}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
          />
        </Box>
      </Box>

      {/* 본문 카드 */}
      <Box sx={PAGE_CARD_SX}>
        {/* 앱 필터 Chip */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setAppName(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: appName === name ? 700 : 400,
                backgroundColor: appName === name ? '#0044D2' : '#fff',
                color: appName === name ? '#fff' : '#374151',
                border: `1px solid ${appName === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': { backgroundColor: appName === name ? '#0033a0' : '#f3f4f6' },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400} gap={2}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography>데이터 로딩 중...</Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            {/* 요약 카드 */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                gap: 2,
                mb: 4,
              }}
            >
              <StatCard
                label="총 당첨자 수"
                value={summary ? fmt(summary.totalWinningUsers) : '—'}
                unit="명"
                tooltip="1~5등 당첨자 수 합계입니다."
              />
              <StatCard
                label="미당첨자 수"
                value={nonWinning ? fmt(nonWinning.nonWinningUserCount) : '—'}
                unit="명"
                tooltip="응모 후 미당첨된 유저 수입니다."
              />
              <StatCard
                label="총 지급 포인트"
                value={summary ? fmt(summary.totalPointAmount) : '—'}
                unit="P"
                tooltip="2~5등 총 지급 포인트 합계입니다."
              />
              <StatCard
                label="미수령 소멸 금액"
                value={summary ? fmt(summary.totalUnclaimedAmount) : '—'}
                unit="원"
                tooltip="수령 기한 내 미수령으로 소멸된 당첨금 합계입니다."
              />
            </Box>

            <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

            {/* 등수별 당첨 현황 */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                  등수별 당첨 현황
                </Typography>
                <Tooltip
                  title="1~5등 등수별 당첨자 수, 수령률, 지급 포인트, 미수령 금액, 후기 작성률을 나타냅니다."
                  arrow
                  placement="right"
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: '#9ca3af', cursor: 'pointer' }} />
                </Tooltip>
              </Box>

              {ranks.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={120}>
                  <Typography color="#94a3b8">데이터가 없습니다.</Typography>
                </Box>
              ) : (
                <TableContainer
                  sx={{ border: '1px solid #f1f5f9', borderRadius: 2, overflow: 'hidden' }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                        {(
                          [
                            { label: '등수' },
                            { label: '당첨 건수' },
                            {
                              label: 'xW',
                              tooltip:
                                '기대 당첨 건수. 기간 내 총 응모 수에 로또 6/45 등수별 확률을 곱한 이론값입니다.',
                            },
                            { label: '당첨자 수' },
                            { label: '수령자 수' },
                            { label: '수령률' },
                            { label: '총 지급 포인트' },
                            {
                              label: 'xP',
                              tooltip:
                                '기대 지급 포인트. 기대 당첨 건수에 실제 1건당 평균 지급 포인트를 곱한 이론값입니다.',
                            },
                            { label: '미수령 소멸 금액' },
                            { label: '후기 작성 수' },
                            { label: '후기 작성률' },
                          ] as { label: string; tooltip?: string }[]
                        ).map(({ label, tooltip }) => (
                          <TableCell
                            key={label}
                            align={label === '등수' ? 'left' : 'right'}
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              color: '#475569',
                              borderBottom: '2px solid #e2e8f0',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}
                            >
                              {label}
                              {tooltip && (
                                <Tooltip title={tooltip} arrow placement="top">
                                  <InfoOutlinedIcon
                                    sx={{ fontSize: 13, color: '#9ca3af', cursor: 'pointer' }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ranks.map((row) => {
                        const style = RANK_STYLE[row.rankNo] ?? { bg: '#f8fafc', color: '#374151' };
                        const showRecipient = row.rankNo >= 2;
                        const showReview = row.rankNo <= 3;
                        const showPoint = row.rankNo >= 2;
                        return (
                          <TableRow
                            key={row.rankNo}
                            hover
                            sx={{ '&:last-child td': { borderBottom: 0 } }}
                          >
                            <TableCell>
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  px: 1.5,
                                  py: 0.4,
                                  borderRadius: 2,
                                  bgcolor: style.bg,
                                  color: style.color,
                                  fontWeight: 700,
                                  fontSize: '0.82rem',
                                }}
                              >
                                {row.rankNo}등
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                              {fmt(row.winningCount)}
                              <Typography
                                component="span"
                                sx={{ fontSize: '0.72rem', color: '#94a3b8', ml: 0.4 }}
                              >
                                건
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem', color: '#64748b' }}>
                              {row.expectedWinningValue.toLocaleString('ko-KR', {
                                maximumFractionDigits: 2,
                              })}
                              <Typography
                                component="span"
                                sx={{ fontSize: '0.72rem', color: '#94a3b8', ml: 0.4 }}
                              >
                                건
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                              {fmt(row.winningUserCount)}
                              <Typography
                                component="span"
                                sx={{ fontSize: '0.72rem', color: '#94a3b8', ml: 0.4 }}
                              >
                                명
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: '0.85rem',
                                color: showRecipient ? '#1e293b' : '#cbd5e1',
                              }}
                            >
                              {showRecipient ? (
                                <>
                                  {fmt(row.winningRecipientCount)}
                                  <Typography
                                    component="span"
                                    sx={{ fontSize: '0.72rem', color: '#94a3b8', ml: 0.4 }}
                                  >
                                    명
                                  </Typography>
                                </>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: 140 }}>
                              {showRecipient ? (
                                <RateBar rate={row.recipientRate} />
                              ) : (
                                <Typography sx={{ fontSize: '0.82rem', color: '#cbd5e1' }}>
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontSize: '0.85rem', color: showPoint ? '#1e293b' : '#cbd5e1' }}
                            >
                              {showPoint ? (
                                <>
                                  {fmt(row.winningPointAmount)}
                                  <Typography
                                    component="span"
                                    sx={{ fontSize: '0.72rem', color: '#94a3b8', ml: 0.4 }}
                                  >
                                    P
                                  </Typography>
                                </>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontSize: '0.85rem', color: showPoint ? '#64748b' : '#cbd5e1' }}
                            >
                              {showPoint ? (
                                <>
                                  {row.expectedPointAmount.toLocaleString('ko-KR', {
                                    maximumFractionDigits: 0,
                                  })}
                                  <Typography
                                    component="span"
                                    sx={{ fontSize: '0.72rem', color: '#94a3b8', ml: 0.4 }}
                                  >
                                    P
                                  </Typography>
                                </>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                              {fmt(row.unclaimedAmount)}
                              <Typography
                                component="span"
                                sx={{ fontSize: '0.72rem', color: '#94a3b8', ml: 0.4 }}
                              >
                                원
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: '0.85rem',
                                color: showReview ? '#1e293b' : '#cbd5e1',
                              }}
                            >
                              {showReview ? (
                                <>
                                  {fmt(row.winningReviewCount)}
                                  <Typography
                                    component="span"
                                    sx={{ fontSize: '0.72rem', color: '#94a3b8', ml: 0.4 }}
                                  >
                                    건
                                  </Typography>
                                </>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ minWidth: 140 }}>
                              {showReview ? (
                                <RateBar rate={row.reviewRate} />
                              ) : (
                                <Typography sx={{ fontSize: '0.82rem', color: '#cbd5e1' }}>
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

            {/* 미당첨 위로 현황 */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                  미당첨 위로 응모권 현황
                </Typography>
                <Tooltip
                  title="미당첨 유저에게 지급되는 위로 응모권 수령 현황입니다."
                  arrow
                  placement="right"
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: '#9ca3af', cursor: 'pointer' }} />
                </Tooltip>
              </Box>

              {!nonWinning ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={80}>
                  <Typography color="#94a3b8">데이터가 없습니다.</Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: 2,
                  }}
                >
                  <StatCard
                    label="미당첨자 수"
                    value={fmt(nonWinning.nonWinningUserCount)}
                    unit="명"
                  />
                  <StatCard
                    label="위로 응모권 수령 수"
                    value={fmt(nonWinning.nonWinningTicketCount)}
                    unit="장"
                  />
                  <Box
                    sx={{
                      backgroundColor: '#f8fafc',
                      borderRadius: 3,
                      p: 2.5,
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500, mb: 1 }}
                    >
                      위로 응모권 수령률
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1.55rem',
                        fontWeight: 700,
                        color: '#0044D2',
                        lineHeight: 1.2,
                        mb: 1,
                      }}
                    >
                      {nonWinning.nonWinningTicketRate.toFixed(1)}%
                    </Typography>
                    <RateBar rate={nonWinning.nonWinningTicketRate} />
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
